'use client'

import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  BanIcon,
  ClockIcon,
  FlameIcon,
  type LucideIcon,
  OctagonAlertIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { SparkleIcon } from '@/components/shell-icons'
import { Skeleton } from '@/components/ui/skeleton'
import {
  HOME_EASE,
  HOME_SPRING,
  HomeHeaderLink,
  HomePanel,
} from '@/features/home/components/home-motion'
import type { HomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { useRelativeTime } from '@/features/home/hooks/use-relative-time'
import {
  type ActivityItem,
  countActivitySince,
  startOfToday,
} from '@/features/home/lib/home-activity'
import { useNow } from '@/hooks/use-now'
import { useNumberFormat } from '@/hooks/use-number-format'
import { getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'

const VISIBLE_ROWS = 7

function iconFor(item: ActivityItem): { Icon: LucideIcon; className: string } {
  switch (item.kind) {
    case 'fill':
      return item.side === 'sell'
        ? { Icon: ArrowUpRightIcon, className: 'bg-chart-red/10 text-chart-red' }
        : { Icon: ArrowDownLeftIcon, className: 'bg-chart-mint/10 text-chart-mint' }
    case 'liquidation':
      return { Icon: FlameIcon, className: 'bg-chart-red/15 text-chart-red' }
    case 'placed':
      return { Icon: ClockIcon, className: 'bg-foreground/5 text-foreground/60' }
    case 'cancelled':
      return { Icon: BanIcon, className: 'bg-foreground/5 text-foreground/45' }
    case 'rejected':
      return { Icon: OctagonAlertIcon, className: 'bg-ox-gold/10 text-ox-gold' }
  }
}

interface HomeActivityProps {
  snapshot: HomeSnapshot
  /** Items after this (epoch ms) get a "new" mark — the previous visit. */
  newSince: number | null
  onOpenTrading: () => void
  onAskWhatHappened: () => void
  className?: string
}

/**
 * "What happened": every account's fills, liquidations, new orders and cancels in one timeline,
 * newest first. A new fill slides in at the top; what arrived since the last visit is marked.
 */
export function HomeActivity({
  snapshot,
  newSince,
  onOpenTrading,
  onAskWhatHappened,
  className,
}: HomeActivityProps) {
  const t = useTranslations('trade.home.activity')
  const fmt = useNumberFormat()
  const relative = useRelativeTime()
  const now = useNow(60_000)
  const { activity, isActivityLoading } = snapshot
  const today = useMemo(() => countActivitySince(activity, startOfToday(now)), [activity, now])
  const todayFills = today.fill + today.liquidation
  const rows = activity.slice(0, VISIBLE_ROWS)

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        <>
          <span className="text-foreground/50 hidden text-xs whitespace-nowrap sm:inline">
            {t('today', { count: todayFills })}
          </span>
          <HomeHeaderLink onClick={onOpenTrading}>{t('viewAll')}</HomeHeaderLink>
        </>
      }
    >
      {isActivityLoading && activity.length === 0 ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-1 flex-col items-start justify-center gap-1 py-6">
          <p className="text-foreground text-sm">{t('emptyTitle')}</p>
          <p className="text-foreground/50 max-w-sm text-xs">{t('emptyHint')}</p>
        </div>
      ) : (
        <ul className="flex flex-col">
          <AnimatePresence initial={false}>
            {rows.map((item, index) => {
              const { Icon, className: iconClass } = iconFor(item)
              const isNew = newSince !== null && item.at > newSince
              return (
                <motion.li
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    height: 'auto',
                    transition: { ...HOME_SPRING, delay: index * 0.03 },
                  }}
                  exit={{ opacity: 0, height: 0, transition: { duration: 0.18, ease: HOME_EASE } }}
                  className="border-foreground/5 border-b last:border-b-0"
                >
                  <div className="flex items-center gap-3 py-2">
                    <span
                      className={cn(
                        'flex size-7 shrink-0 items-center justify-center rounded-full',
                        iconClass
                      )}
                    >
                      <Icon className="size-3.5" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-foreground truncate text-sm tracking-tight">
                        {t(`kinds.${item.kind}`, {
                          action: t(
                            `verbs.${item.kind === 'fill' ? 'fill' : 'order'}.${item.side ?? 'none'}`
                          ),
                          quantity: fmt.text(item.quantity.replace(/^-/, ''), { sensitive: true }),
                          symbol: item.symbol,
                        })}
                      </span>
                      <span className="text-foreground/45 truncate text-xs">
                        {item.price
                          ? t('at', {
                              price: fmt.number(item.price, { maximumFractionDigits: 8 }),
                              venue: getExchangeLabel(item.exchange),
                            })
                          : getExchangeLabel(item.exchange)}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {isNew && (
                        <span className="bg-shell-accent/15 text-shell-accent rounded-full px-1.5 py-0.5 text-[10px] font-medium">
                          {t('new')}
                        </span>
                      )}
                      <span className="text-foreground/40 text-xs whitespace-nowrap">
                        {relative(item.at)}
                      </span>
                    </span>
                  </div>
                </motion.li>
              )
            })}
          </AnimatePresence>
        </ul>
      )}
      <button
        type="button"
        onClick={onAskWhatHappened}
        className="group text-shell-accent hover:bg-shell-accent/10 mt-auto flex cursor-pointer items-center justify-center gap-2 rounded-md pt-3 pb-2 text-xs font-medium transition-colors"
      >
        <SparkleIcon className="size-3.5 transition-transform duration-300 ease-(--ease-spring) group-hover:rotate-90" />
        {t('askAi')}
      </button>
    </HomePanel>
  )
}
